import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';

import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';

import { ChecklistItem, CoverDataItem } from '../../../../utils/models';
import { formatNumberRange, getChunkedData } from '../../../../utils/utilities';

import classNames from './style.module.scss';

interface ChecklistProps {
	enableChecklistItemComment: boolean;
	headers: CoverDataItem[];
	additionalComment: string;
	checklistItems: ChecklistItem[];
	sectionTitle?: string;
	sectionSubtitle?: string;
}

const Checklist: React.FC<ChecklistProps> = ({ enableChecklistItemComment, additionalComment, headers, checklistItems, sectionSubtitle = "Checklist", sectionTitle = "Inspection Findings" }) => {
	const getAssessmentText = useCallback((columnName: string, assessment: string) => {
		if (columnName === 'ok') {
			if (assessment === 'ok') return 'OK';

			if (assessment === 'na') return 'N/A';

			return '-';
		}

		if (columnName === 'anomaly') {
			if (assessment === 'anomaly') return 'X';

			if (assessment === 'na') return 'N/A';

			return '-';
		}

		if (columnName === 'anomaly-no') {
			if (assessment === 'anomaly-no') return 'XX';

			if (assessment === 'na') return 'N/A';

			return '-';
		}
	}, []);

	const renderChecklistItems = useCallback((itemsToRender: ChecklistItem[]) => {
		return itemsToRender
				.map((item) => (
					<tr key={item.id}>
						<td>{item.id}</td>
						<td>{item.lineNumber}</td>
						{['ok', 'anomaly'].map((assessment) => (
							<td key={assessment} className="text-center">
								{getAssessmentText(assessment, item.assessment)}
							</td>
						))}
						<Conditional isVisible={enableChecklistItemComment}>
							<td className="text-center">
								{item.comment}
							</td>
						</Conditional>
						<td className="text-center">
							{formatNumberRange(item.anomalyItems.map((anomaly) => anomaly.id))}
						</td>
					</tr>
				));
	}, [getAssessmentText, enableChecklistItemComment]);

	const renderGroupedChecklistItems = useCallback((groupedItems: _.Dictionary<ChecklistItem[]>) => {
		return (
			<>
				{Object.keys(groupedItems).map(key => {
					return (
						<Conditional key={key} isVisible={groupedItems[key].length > 0}>
							<tr>
								<td className={classNames.checklistTableCaption} colSpan={enableChecklistItemComment ? 6 : 5}>
									{key.split('-').join(' ')}
								</td>
							</tr>
	
							{renderChecklistItems(groupedItems[key])}
						</Conditional>
					)
				})}
			</>
		);
	}, [renderChecklistItems, enableChecklistItemComment]);

	const chunkedGroups = useMemo(() => {
		const sortedChecklistItems = _.orderBy(checklistItems, (checklist) => parseInt(checklist.id), ['asc']);
		return getChunkedData(sortedChecklistItems, 20).map((chunk) => _.groupBy(chunk, 'category'));
	}, [checklistItems]);

	return (
		<>
			{chunkedGroups.map((group, index) => (
				<CustomSection key={index} sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle}>
					<table className={classNames.checklistTable}>
						<thead>
							{getChunkedData(headers, 2).map(([firstHeader, secondHeader], index) => (
								<React.Fragment key={index}>
									<tr>
										<th rowSpan={2}>Item</th>
										
										<th colSpan={2} className={classNames.checklistTableTenderBlueBG}>
											{firstHeader.label}: <span className="mx-2 font-uppercase">{firstHeader.value}</span>
										</th>
										
										<th colSpan={enableChecklistItemComment ? 4 : 3}>Assessment</th>
									</tr>
							
									<tr>
										<th className={classNames.checklistTableTenderBlueBG}>
											{secondHeader && (
												<React.Fragment>
													{secondHeader.label}: <span className="mx-2 font-uppercase">{secondHeader.value}</span>
												</React.Fragment>
											)}
										</th>
										
										<th>Ok</th>
										
										<th>Anomaly</th>
										
										<Conditional isVisible={enableChecklistItemComment}>
											<th>Comment</th>
										</Conditional>
									
										<th>Anomaly No</th>
									</tr>
								</React.Fragment>
							))}
						</thead>
						
						<tbody>
							{renderGroupedChecklistItems(group)}
						</tbody>
					</table>

					<Conditional isVisible={additionalComment ? additionalComment.length > 0 : false}>
						<div className={classNames.checklistTableComment}>
							<div className={classNames.checklistTableCommentHeader}>
								Additional comment, if any
							</div>
							<div className={classNames.checklistTableCommentBody}>
								{additionalComment}
							</div>
						</div>
					</Conditional>
				</CustomSection>
			))}
		</>
	);
};

export default Checklist;
